import {
  Box,
  Button,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { TagContainer } from 'SandboxModule/styles';
import { standardTagsList } from '@constants';
import { LinearBuffer } from 'components/Progress';
import { BaseDivider } from 'components/BaseDivider';
import SearchableTenantSelect from 'TeamModule/components/Filters/SearchableTenantSelect';
import TeamTagsAutocomplete from 'components/TeamTagsAutocomplete';
import { useSandboxModalFields } from 'SandboxModule/hooks/useSandboxModalFields';
import { CreateRealmParams } from 'SandboxModule/types';
import { useRealmIdGeneration } from 'SandboxModule/hooks/useRealmIdGeneration';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'selectors';
import { RealmTypes } from 'RealmModule/constants/realm';
import { useCreateRealm } from 'SandboxModule/hooks/useCreateRealm';
import { useNotification } from 'hooks/useNotification';

export type NewSandboxInitialValues = Omit<
  CreateRealmParams,
  'realmType' | 'metaPayload' | 'fileName'
>;

const defaultRegion = 'us-east-1';
const defaultDomain = 'mosaicapp.com';

const newSandboxInitialValues: NewSandboxInitialValues = {
  realmId: '',
  teamName: '',
  region: '',
  domain: '',
};

export const CreateSandboxForm: React.FC<{
  onRealmCreateSuccess: () => void;
}> = ({ onRealmCreateSuccess }) => {
  // Tenant selection state (cascading selector pattern)
  const [selectedTenant, setSelectedTenant] = useState<string>('');

  const { handleTagCheckboxChange, standardTags, tagsList, applyTagsChange, tagError } =
    useSandboxModalFields();
  const [newSandboxValues, setNewSandboxValues] =
    useState<NewSandboxInitialValues>(newSandboxInitialValues);
  const { createRealm, isCreatingRealm } = useCreateRealm({
    onSuccess: onRealmCreateSuccess,
  });
  const user = useSelector(selectAuthUser);
  const { showSuccess } = useNotification();

  const generateRealmIdOnSuccess: Parameters<typeof useRealmIdGeneration>[0]['onSuccess'] = ({
    response,
  }) => {
    const realmId = response.data;
    if (realmId && user && selectedTenant) {
      const params: CreateRealmParams = {
        realmId,
        teamName: newSandboxValues.teamName,
        domain: defaultDomain,
        region: defaultRegion, // Hard-coded - May need it to be dynamic in the future
        realmType: RealmTypes.sandbox,
        tenantName: selectedTenant,
        tags: tagsList,
        metaPayload: { name: user.account.email }, // Intentionally using the account email instead of the name to ensure accuracy
      };

      showSuccess(
        `Initiating creating new sandbox: ${newSandboxValues.teamName}. It will take a while.`,
      );
      createRealm(params);
    }
  };

  // on form change
  const onCreateSandboxFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setNewSandboxValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const { generateRealmId } = useRealmIdGeneration({
    onSuccess: generateRealmIdOnSuccess,
  });

  const handleSubmitCreateSandboxForm = () => {
    generateRealmId({
      teamName: newSandboxValues.teamName,
    });
  };

  return (
    <form>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Organization
        </Typography>
        <SearchableTenantSelect
          value={selectedTenant}
          onChange={setSelectedTenant}
          label="Tenant"
          labelId="create-sandbox-tenant-select"
          hideAllOption={true}
        />
      </Box>
      <TextField
        required
        onChange={onCreateSandboxFormChange}
        value={newSandboxValues.teamName}
        label="Team Name"
        name="teamName"
        type="text"
        fullWidth
        variant="outlined"
      />
      <BaseDivider />

      <FormControl component="fieldset" fullWidth>
        <Box component="h3">Add tags to this team</Box>
        <TagContainer>
          <div className="initial-tag-div">
            <FormGroup>
              {standardTagsList.map((item) => (
                <FormControlLabel
                  key={`${item.value}-${standardTags[item.value as keyof typeof standardTags]}`}
                  control={
                    <Checkbox
                      checked={standardTags[item.value as keyof typeof standardTags]}
                      onChange={handleTagCheckboxChange}
                      name={item.name}
                      value={item.value}
                      disabled={isCreatingRealm}
                    />
                  }
                  label={item.label}
                />
              ))}
            </FormGroup>
          </div>
          <div className="add-tag-div">
            <TeamTagsAutocomplete
              value={tagsList}
              onChange={applyTagsChange}
              helperText={tagError}
              error={!!tagError}
              disabled={isCreatingRealm}
              placeholder="Select or type a tag"
            />
          </div>
          {/* ----------------------------------- for start date ---------------------------------- */}
          {/* <div className="start-date">
              <TextField
                value={tagInput}
                onKeyDown={addTag(tagInput)}
                helperText="Start Date"
                onChange={handleInputChange}
                disabled={isCreatingTeam}
                type="date"
              />
            </div> */}
        </TagContainer>
      </FormControl>

      <Box>{isCreatingRealm ? <LinearBuffer /> : null}</Box>

      <Box display={'flex'} justifyContent={'end'}>
        <Button
          size="large"
          disabled={isCreatingRealm || !!tagError}
          onClick={handleSubmitCreateSandboxForm}
          color="primary"
        >
          Submit
        </Button>
      </Box>
    </form>
  );
};
